import { fhirR4 } from "@smile-cdr/fhirts";
import { FHIR_UNITS } from "@vitallhealth/fhir-core";

import {
  CONDITION_CODING,
  HEART_FAILURE_THRESHOLD_SYSTEM,
  PATIENT_CONDITION_URLS,
  PATIENT_EXT_ICD,
  PATIENT_EXT_LVAD,
  PATIENT_EXT_MEDICATION,
  PATIENT_EXT_MEDICATION_INFO,
  THRESHOLD_DISPLAY,
  ThresholdCode,
  UNIT_LBS,
  VITAL_CODES,
} from "./fhir-constants";
import { FhirResourceType, HttpMethod, formatters } from "./fhir-utils";
import {
  DEFAULT_WEIGHT_UNIT,
  WEIGHT_MAX_DELTA,
  WEIGHT_THRESHOLD_DECIMALS,
  type WeightUnit,
  readingToLb,
  storedWeightUnit,
} from "./weight-units";

// Re-export constants for backward compatibility
export {
  CONDITION_CODING,
  VITAL_CODES,
  PATIENT_EXT_ICD,
  PATIENT_EXT_LVAD,
  PATIENT_EXT_MEDICATION,
  PATIENT_EXT_MEDICATION_INFO,
} from "./fhir-constants";

// ============================================================
// CONSTANTS - Threshold Levels
// ============================================================

export const THRESHOLD_LOW: fhirR4.Coding = {
  system: HEART_FAILURE_THRESHOLD_SYSTEM,
  code: ThresholdCode.LOW,
  display: THRESHOLD_DISPLAY[ThresholdCode.LOW],
};
export const THRESHOLD_HIGH: fhirR4.Coding = {
  system: HEART_FAILURE_THRESHOLD_SYSTEM,
  code: ThresholdCode.HIGH,
  display: THRESHOLD_DISPLAY[ThresholdCode.HIGH],
};
export const THRESHOLD_CRITICALLY_LOW: fhirR4.Coding = {
  system: HEART_FAILURE_THRESHOLD_SYSTEM,
  code: ThresholdCode.CRITICALLY_LOW,
  display: THRESHOLD_DISPLAY[ThresholdCode.CRITICALLY_LOW],
};

// ============================================================
// INTERFACES
// ============================================================

export interface WeightParameters {
  minimum?: number;
  maximum?: number;
  maxDelta?: number;
}

export interface HeartRateParameters {
  minimum?: number;
  maximum?: number;
}

export interface BloodPressureParameters {
  systolicMinimum?: number;
  systolicMaximum?: number;
  systolicUrgentLow?: number;
  diastolicMinimum?: number;
  diastolicMaximum?: number;
}

export interface PatientConditions {
  hasHeartFailure?: boolean;
  hasICD: boolean;
  hasLVAD: boolean;
  takesMedicationForWeightGain: boolean;
  medicationInfo?: string;
}

export interface ConditionParametersFormData {
  carePlanId?: string;
  weight: WeightParameters;
  heartRate: HeartRateParameters;
  bloodPressure: BloodPressureParameters;
  conditions: PatientConditions;
}

export const emptyConditionParametersState = (): ConditionParametersFormData => ({
  carePlanId: undefined,
  weight: {},
  heartRate: {},
  bloodPressure: {},
  conditions: {
    hasHeartFailure: false,
    hasICD: false,
    hasLVAD: false,
    takesMedicationForWeightGain: false,
    medicationInfo: "",
  },
});

// ============================================================
// GOAL PARSING HELPERS
// ============================================================

/**
 * Find a Goal by vital sign code and optional threshold level.
 * Vital is matched by its own system (LOINC or SNOMED).
 * Threshold is matched by the heartFailureThreshold system.
 * When thresholdCode is omitted (e.g. weight delta), only vital is matched.
 */
export const findGoalByVitalAndThreshold = (
  goals: fhirR4.Goal[],
  vitalCode: string,
  thresholdCode?: string
): fhirR4.Goal | undefined => {
  return goals.find(goal => {
    const coding = goal.target?.[0]?.measure?.coding ?? [];
    const hasVital = coding.some(c => c.code === vitalCode);
    if (!thresholdCode) {
      return hasVital;
    }
    const hasThreshold = coding.some(c => c.system === HEART_FAILURE_THRESHOLD_SYSTEM && c.code === thresholdCode);
    return hasVital && hasThreshold;
  });
};

/**
 * Check if a Goal is a threshold goal (has a recognized vital sign measure)
 */
export const isThresholdGoal = (goal: fhirR4.Goal): boolean => {
  const coding = goal.target?.[0]?.measure?.coding ?? [];
  const knownCodes = Object.values(VITAL_CODES).map(v => v.code);
  return coding.some(c => knownCodes.includes(c.code ?? ""));
};

/**
 * Check if a CarePlan is a threshold care plan
 */
export const isThresholdCarePlan = (carePlan: fhirR4.CarePlan): boolean => {
  return (
    carePlan.category?.some(cat =>
      cat.coding?.some(c => c.system === CONDITION_CODING.snomed.system && c.code === CONDITION_CODING.snomed.code)
    ) ?? false
  );
};

// ============================================================
// PARSING: Goals → Form Data
// ============================================================

/**
 * Parse an array of Goal resources into condition parameters form data.
 * Each parameter maps to a separate Goal with a single detailQuantity value.
 */
export const goalsToFormData = (
  goals: fhirR4.Goal[]
): Pick<ConditionParametersFormData, "weight" | "heartRate" | "bloodPressure"> => {
  const result: Pick<ConditionParametersFormData, "weight" | "heartRate" | "bloodPressure"> = {
    weight: {},
    heartRate: {},
    bloodPressure: {},
  };

  // Body Weight Low → weight.minimum
  const weightLow = findGoalByVitalAndThreshold(goals, VITAL_CODES.bodyWeight.code!, THRESHOLD_LOW.code!);
  if (weightLow) {
    result.weight.minimum = readingToLb(weightLow.target?.[0]?.detailQuantity);
  }

  // Body Weight High → weight.maximum
  const weightHigh = findGoalByVitalAndThreshold(goals, VITAL_CODES.bodyWeight.code!, THRESHOLD_HIGH.code!);
  if (weightHigh) {
    result.weight.maximum = readingToLb(weightHigh.target?.[0]?.detailQuantity);
  }

  // Body Weight Delta → weight.maxDelta (no threshold code)
  const deltaGoal = findGoalByVitalAndThreshold(goals, VITAL_CODES.bodyWeightDelta.code!);
  if (deltaGoal) {
    result.weight.maxDelta = readingToLb(deltaGoal.target?.[0]?.detailQuantity);
  }

  // Systolic Low → bloodPressure.systolicMinimum
  const systolicLow = findGoalByVitalAndThreshold(goals, VITAL_CODES.systolic.code!, THRESHOLD_LOW.code!);
  if (systolicLow) {
    result.bloodPressure.systolicMinimum = systolicLow.target?.[0]?.detailQuantity?.value;
  }

  // Systolic High → bloodPressure.systolicMaximum
  const systolicHigh = findGoalByVitalAndThreshold(goals, VITAL_CODES.systolic.code!, THRESHOLD_HIGH.code!);
  if (systolicHigh) {
    result.bloodPressure.systolicMaximum = systolicHigh.target?.[0]?.detailQuantity?.value;
  }

  // Systolic Critically Low → bloodPressure.systolicUrgentLow
  const systolicCritical = findGoalByVitalAndThreshold(
    goals,
    VITAL_CODES.systolic.code!,
    THRESHOLD_CRITICALLY_LOW.code!
  );
  if (systolicCritical) {
    result.bloodPressure.systolicUrgentLow = systolicCritical.target?.[0]?.detailQuantity?.value;
  }

  // Diastolic Low → bloodPressure.diastolicMinimum
  const diastolicLow = findGoalByVitalAndThreshold(goals, VITAL_CODES.diastolic.code!, THRESHOLD_LOW.code!);
  if (diastolicLow) {
    result.bloodPressure.diastolicMinimum = diastolicLow.target?.[0]?.detailQuantity?.value;
  }

  // Diastolic High → bloodPressure.diastolicMaximum
  const diastolicHigh = findGoalByVitalAndThreshold(goals, VITAL_CODES.diastolic.code!, THRESHOLD_HIGH.code!);
  if (diastolicHigh) {
    result.bloodPressure.diastolicMaximum = diastolicHigh.target?.[0]?.detailQuantity?.value;
  }

  // Heart Rate (Pulse) Low → heartRate.minimum
  const pulseLow = findGoalByVitalAndThreshold(goals, VITAL_CODES.pulse.code!, THRESHOLD_LOW.code!);
  if (pulseLow) {
    result.heartRate.minimum = pulseLow.target?.[0]?.detailQuantity?.value;
  }

  // Heart Rate (Pulse) High → heartRate.maximum
  const pulseHigh = findGoalByVitalAndThreshold(goals, VITAL_CODES.pulse.code!, THRESHOLD_HIGH.code!);
  if (pulseHigh) {
    result.heartRate.maximum = pulseHigh.target?.[0]?.detailQuantity?.value;
  }

  return result;
};

// ============================================================
// PARSING: Patient → Patient Conditions
// ============================================================

/**
 * Parse Patient extensions to get patient conditions
 */
export const patientToConditions = (patient: fhirR4.Patient | undefined): PatientConditions => {
  const defaults: PatientConditions = {
    hasHeartFailure: false,
    hasICD: false,
    hasLVAD: false,
    takesMedicationForWeightGain: false,
    medicationInfo: "",
  };

  if (!patient?.extension) {
    return defaults;
  }

  const hasICD = patient.extension.find(e => e.url === PATIENT_EXT_ICD)?.valueBoolean ?? false;
  const hasLVAD = patient.extension.find(e => e.url === PATIENT_EXT_LVAD)?.valueBoolean ?? false;
  const takesMedicationForWeightGain =
    patient.extension.find(e => e.url === PATIENT_EXT_MEDICATION)?.valueBoolean ?? false;

  return {
    hasHeartFailure: hasICD || hasLVAD || takesMedicationForWeightGain,
    hasICD,
    hasLVAD,
    takesMedicationForWeightGain,
    medicationInfo: patient.extension.find(e => e.url === PATIENT_EXT_MEDICATION_INFO)?.valueString ?? "",
  };
};

/**
 * Update a Patient resource with condition extensions (preserves other extensions)
 */
export const updatePatientConditionExtensions = (
  patient: fhirR4.Patient,
  conditions: PatientConditions
): fhirR4.Patient => {
  const updated = JSON.parse(JSON.stringify(patient)) as fhirR4.Patient;

  const otherExtensions = (updated.extension ?? []).filter(ext => !PATIENT_CONDITION_URLS.includes(ext.url ?? ""));
  const conditionExtensions: fhirR4.Extension[] = [
    { url: PATIENT_EXT_ICD, valueBoolean: conditions.hasICD },
    { url: PATIENT_EXT_LVAD, valueBoolean: conditions.hasLVAD },
    { url: PATIENT_EXT_MEDICATION, valueBoolean: conditions.takesMedicationForWeightGain },
  ];

  if (conditions.medicationInfo) {
    conditionExtensions.push({ url: PATIENT_EXT_MEDICATION_INFO, valueString: conditions.medicationInfo });
  }

  updated.extension = [...otherExtensions, ...conditionExtensions];
  return updated;
};

// ============================================================
// UPDATING: Existing Goal Values
// ============================================================

/** Vital codes whose Goal values are weights, and so are stored in lb. */
const WEIGHT_VITAL_CODES: string[] = [VITAL_CODES.bodyWeight.code!, VITAL_CODES.bodyWeightDelta.code!];

/**
 * Update an existing Goal's target value via deep clone.
 * Only updates the value field, preserving all server-provided fields
 * (meta, extensions, notes, measure coding, unit info, etc.).
 * Goals are created by the backend during patient onboarding — the frontend only updates values.
 *
 * The one exception is a weight Goal's unit: form values are lb (goalsToFormData
 * converts whatever the Goal carried), so a Goal stored in another unit, or
 * with none, is written back as lb. A Goal already in lb keeps its own fields.
 */
const updateExistingGoalValue = (existingGoal: fhirR4.Goal, value: number, isWeight: boolean): fhirR4.Goal => {
  const cloned: fhirR4.Goal = JSON.parse(JSON.stringify(existingGoal));

  if (!cloned.target || cloned.target.length === 0) {
    cloned.target = [{}];
  }

  const quantity = (cloned.target[0].detailQuantity ??= {});
  quantity.value = value;
  if (isWeight && storedWeightUnit(quantity) !== FHIR_UNITS.LBS) {
    Object.assign(quantity, UNIT_LBS);
  }

  return cloned;
};

// ============================================================
// CONVERT: Form Data → Goals Array
// ============================================================

export interface GoalWithMeta {
  goal: fhirR4.Goal;
  vitalCode: string;
  thresholdCode?: string;
}

export interface FormDataToGoalsResult {
  goals: GoalWithMeta[];
  missingGoals: string[];
}

/**
 * Convert form data to an array of updated Goal resources.
 * Only updates existing goals. If a form value has no matching existing goal,
 * its description is added to `missingGoals` so the caller can show a notification.
 * Goals are created by the backend; the frontend only updates values.
 */
export const formDataToGoals = (
  data: ConditionParametersFormData,
  existingGoals: fhirR4.Goal[]
): FormDataToGoalsResult => {
  const goals: GoalWithMeta[] = [];
  const missingGoals: string[] = [];

  const tryUpdate = (value: number | undefined, vitalCode: string, thresholdCode?: string) => {
    if (value === undefined) {
      return;
    }
    const existing = findGoalByVitalAndThreshold(existingGoals, vitalCode, thresholdCode);
    if (!existing) {
      missingGoals.push(`vital "${vitalCode}"${thresholdCode ? ` threshold "${thresholdCode}"` : ""}`);
      return;
    }
    goals.push({
      goal: updateExistingGoalValue(existing, value, WEIGHT_VITAL_CODES.includes(vitalCode)),
      vitalCode,
      thresholdCode,
    });
  };

  tryUpdate(data.weight.minimum, VITAL_CODES.bodyWeight.code!, THRESHOLD_LOW.code!);
  tryUpdate(data.weight.maximum, VITAL_CODES.bodyWeight.code!, THRESHOLD_HIGH.code!);
  tryUpdate(data.weight.maxDelta, VITAL_CODES.bodyWeightDelta.code!);
  tryUpdate(data.bloodPressure.systolicMinimum, VITAL_CODES.systolic.code!, THRESHOLD_LOW.code!);
  tryUpdate(data.bloodPressure.systolicMaximum, VITAL_CODES.systolic.code!, THRESHOLD_HIGH.code!);
  tryUpdate(data.bloodPressure.systolicUrgentLow, VITAL_CODES.systolic.code!, THRESHOLD_CRITICALLY_LOW.code!);
  tryUpdate(data.bloodPressure.diastolicMinimum, VITAL_CODES.diastolic.code!, THRESHOLD_LOW.code!);
  tryUpdate(data.bloodPressure.diastolicMaximum, VITAL_CODES.diastolic.code!, THRESHOLD_HIGH.code!);
  tryUpdate(data.heartRate.minimum, VITAL_CODES.pulse.code!, THRESHOLD_LOW.code!);
  tryUpdate(data.heartRate.maximum, VITAL_CODES.pulse.code!, THRESHOLD_HIGH.code!);

  return { goals, missingGoals };
};

// ============================================================
// BUNDLE: Build transaction Bundle for saving all resources
// ============================================================

/**
 * Build a FHIR transaction Bundle that updates existing Goals and Patient conditions.
 * Goals and CarePlan are created by the backend — this only PUTs goal value updates and patient extensions.
 */
/**
 * Optional version overrides for If-Match headers in the transaction bundle.
 * When provided, these take precedence over the `meta.versionId` on the resources,
 * which may be stale due to HealthLake eventual consistency.
 */
export interface SaveBundleVersionOverrides {
  /** Map of Goal.id → latest known versionId */
  goalVersions?: Map<string, string>;
  /** Latest known Patient versionId */
  patientVersion?: string;
}

export const buildSaveBundle = (
  patientId: string,
  formData: ConditionParametersFormData,
  existingGoals: fhirR4.Goal[],
  patient: fhirR4.Patient,
  versionOverrides?: SaveBundleVersionOverrides
): fhirR4.Bundle => {
  const entries: fhirR4.BundleEntry[] = [];

  // Update existing Goal entries (all goals already exist on the server)
  const { goals: goalsToSave } = formDataToGoals(formData, existingGoals);

  for (const { goal } of goalsToSave) {
    const goalRef = formatters.formatGoal(goal.id!);
    const version = versionOverrides?.goalVersions?.get(goal.id!) ?? goal.meta?.versionId;
    entries.push({
      fullUrl: goalRef,
      resource: goal,
      request: {
        method: HttpMethod.PUT,
        url: goalRef,
        ...(version && { ifMatch: `W/"${version}"` }),
      },
    });
  }

  // Update Patient with condition extensions
  const updatedPatient = updatePatientConditionExtensions(patient, formData.conditions);
  const patientRef = formatters.formatPatient(updatedPatient.id ?? "");
  const patientVersion = versionOverrides?.patientVersion ?? patient.meta?.versionId;
  entries.push({
    fullUrl: patientRef,
    resource: updatedPatient,
    request: {
      method: HttpMethod.PUT,
      url: patientRef,
      ...(patientVersion && { ifMatch: `W/"${patientVersion}"` }),
    },
  });

  return {
    resourceType: FhirResourceType.Bundle,
    type: "transaction",
    entry: entries,
  };
};

// ============================================================
// BUNDLE RESPONSE HELPERS
// ============================================================

/** Check if any server goal version differs from known tracked versions */
export const hasGoalVersionMismatch = (serverGoals: fhirR4.Goal[], knownVersions: Map<string, string>): boolean =>
  serverGoals.some(serverGoal => {
    if (!serverGoal.id || !serverGoal.meta?.versionId) {
      return false;
    }
    const known = knownVersions.get(serverGoal.id);
    return known && known !== serverGoal.meta.versionId;
  });

/** Check if the server patient version differs from a known tracked version */
export const hasPatientVersionMismatch = (
  serverPatient: fhirR4.Patient | undefined,
  knownVersion: string | undefined
): boolean => !!serverPatient?.meta?.versionId && !!knownVersion && serverPatient.meta.versionId !== knownVersion;

// ============================================================
// VALIDATION
// ============================================================

export type FieldErrors = Record<string, string>;

/** Check if a number has more than `maxDecimals` decimal places */
export const hasExcessiveDecimals = (value: number, maxDecimals: number): boolean => {
  const parts = String(value).split(".");
  return parts.length > 1 && parts[1].length > maxDecimals;
};

// ---- Internal validation helpers (mutate errors, skip if already errored) ----

/** Set error on a field if the value is defined, not already errored, and fails the check */
const checkField = (
  errors: FieldErrors,
  key: string,
  value: number | undefined,
  test: (v: number) => boolean,
  message: string
) => {
  if (value !== undefined && !errors[key] && test(value)) {
    errors[key] = message;
  }
};

/** Set required error if value is undefined */
const checkRequired = (errors: FieldErrors, key: string, value: number | undefined, message: string) => {
  if (value === undefined) {
    errors[key] = message;
  }
};

/** Set error on minKey if min >= max (both must be defined and error-free) */
const checkMinLessThanMax = (
  errors: FieldErrors,
  minKey: string,
  maxKey: string,
  min: number | undefined,
  max: number | undefined,
  message: string
) => {
  if (min !== undefined && max !== undefined && !errors[minKey] && !errors[maxKey] && min >= max) {
    errors[minKey] = message;
  }
};

// ---- Validation sub-routines ----

const T_BASE = "pages.patientOverview.conditionParameters.validation";

/**
 * Refine's translate: `(key, fallback)` or `(key, options, fallback)`, where
 * options are interpolated into the message (`{{max}}`).
 */
export type ValidationTranslate = (
  key: string,
  fallbackOrOptions?: string | Record<string, unknown>,
  fallback?: string
) => string;

const validateWeight = (
  errors: FieldErrors,
  weight: WeightParameters,
  translate: ValidationTranslate,
  isRequired: boolean,
  weightUnit: WeightUnit
) => {
  const positive = translate(`${T_BASE}.weightPositive`, "Must be a positive number");
  const required = translate(`${T_BASE}.required`, "Required");
  const maxDecimals = WEIGHT_THRESHOLD_DECIMALS[weightUnit];
  const tooManyDecimals =
    weightUnit === FHIR_UNITS.KG
      ? translate(`${T_BASE}.weightMaxTwoDecimals`, "Up to 2 decimal places allowed")
      : translate(`${T_BASE}.weightMaxOneDecimal`, "Up to 1 decimal place allowed");

  if (isRequired) {
    checkRequired(errors, "weight.minimum", weight.minimum, required);
    checkRequired(errors, "weight.maximum", weight.maximum, required);
    checkRequired(errors, "weight.maxDelta", weight.maxDelta, required);
  }

  checkField(errors, "weight.minimum", weight.minimum, v => v <= 0, positive);
  checkField(errors, "weight.maximum", weight.maximum, v => v <= 0, positive);
  const maxDelta = WEIGHT_MAX_DELTA[weightUnit];
  checkField(
    errors,
    "weight.maxDelta",
    weight.maxDelta,
    v => v < 0 || v > maxDelta,
    translate(`${T_BASE}.maxDeltaBound`, { max: maxDelta }, `Must be between 0 and ${maxDelta}`)
  );

  checkField(errors, "weight.minimum", weight.minimum, v => hasExcessiveDecimals(v, maxDecimals), tooManyDecimals);
  checkField(errors, "weight.maximum", weight.maximum, v => hasExcessiveDecimals(v, maxDecimals), tooManyDecimals);
  checkField(errors, "weight.maxDelta", weight.maxDelta, v => hasExcessiveDecimals(v, maxDecimals), tooManyDecimals);

  checkMinLessThanMax(
    errors,
    "weight.minimum",
    "weight.maximum",
    weight.minimum,
    weight.maximum,
    translate(`${T_BASE}.weightMinLessThanMax`, "Minimum must be less than maximum")
  );
};

const validateHeartRate = (
  errors: FieldErrors,
  heartRate: HeartRateParameters,
  translate: ValidationTranslate,
  isRequired: boolean
) => {
  const wholeNumber = translate(`${T_BASE}.mustBeWholeNumber`, "Must be a whole number");
  const required = translate(`${T_BASE}.required`, "Required");

  if (isRequired) {
    checkRequired(errors, "heartRate.minimum", heartRate.minimum, required);
    checkRequired(errors, "heartRate.maximum", heartRate.maximum, required);
  }

  const heartRateBound = translate(`${T_BASE}.heartRateBound`, "Must be between 30 and 220");
  checkField(errors, "heartRate.minimum", heartRate.minimum, v => v < 30 || v > 220, heartRateBound);
  checkField(errors, "heartRate.maximum", heartRate.maximum, v => v < 30 || v > 220, heartRateBound);

  checkMinLessThanMax(
    errors,
    "heartRate.minimum",
    "heartRate.maximum",
    heartRate.minimum,
    heartRate.maximum,
    translate(`${T_BASE}.heartRateMinLessThanMax`, "Minimum must be less than maximum")
  );

  checkField(errors, "heartRate.minimum", heartRate.minimum, v => !Number.isInteger(v), wholeNumber);
  checkField(errors, "heartRate.maximum", heartRate.maximum, v => !Number.isInteger(v), wholeNumber);
};

const validateBloodPressure = (
  errors: FieldErrors,
  bp: BloodPressureParameters,
  translate: ValidationTranslate,
  isRequired: boolean
) => {
  const wholeNumber = translate(`${T_BASE}.mustBeWholeNumber`, "Must be a whole number");
  const systolicBound = translate(`${T_BASE}.systolicBound`, "Must be between 60 and 250");
  const diastolicBound = translate(`${T_BASE}.diastolicBound`, "Must be between 40 and 150");
  const required = translate(`${T_BASE}.required`, "Required");

  // Systolic
  if (isRequired) {
    checkRequired(errors, "bp.systolicMinimum", bp.systolicMinimum, required);
    checkRequired(errors, "bp.systolicMaximum", bp.systolicMaximum, required);
    checkRequired(errors, "bp.systolicUrgentLow", bp.systolicUrgentLow, required);
  }

  checkField(errors, "bp.systolicMinimum", bp.systolicMinimum, v => v < 60 || v > 250, systolicBound);
  checkField(errors, "bp.systolicMaximum", bp.systolicMaximum, v => v < 60 || v > 250, systolicBound);

  checkMinLessThanMax(
    errors,
    "bp.systolicMinimum",
    "bp.systolicMaximum",
    bp.systolicMinimum,
    bp.systolicMaximum,
    translate(`${T_BASE}.systolicMinLessThanMax`, "Minimum must be less than maximum")
  );

  // Urgent low must be strictly less than systolic min
  if (
    bp.systolicUrgentLow !== undefined &&
    bp.systolicMinimum !== undefined &&
    bp.systolicUrgentLow >= bp.systolicMinimum
  ) {
    errors["bp.systolicUrgentLow"] = translate(`${T_BASE}.urgentLowBound`, "Must be less than systolic minimum");
  }

  checkField(errors, "bp.systolicMinimum", bp.systolicMinimum, v => !Number.isInteger(v), wholeNumber);
  checkField(errors, "bp.systolicMaximum", bp.systolicMaximum, v => !Number.isInteger(v), wholeNumber);
  checkField(errors, "bp.systolicUrgentLow", bp.systolicUrgentLow, v => !Number.isInteger(v), wholeNumber);

  // Diastolic
  if (isRequired) {
    checkRequired(errors, "bp.diastolicMinimum", bp.diastolicMinimum, required);
    checkRequired(errors, "bp.diastolicMaximum", bp.diastolicMaximum, required);
  }

  checkField(errors, "bp.diastolicMinimum", bp.diastolicMinimum, v => v < 40 || v > 150, diastolicBound);
  checkField(errors, "bp.diastolicMaximum", bp.diastolicMaximum, v => v < 40 || v > 150, diastolicBound);

  checkMinLessThanMax(
    errors,
    "bp.diastolicMinimum",
    "bp.diastolicMaximum",
    bp.diastolicMinimum,
    bp.diastolicMaximum,
    translate(`${T_BASE}.diastolicMinLessThanMax`, "Minimum must be less than maximum")
  );

  checkField(errors, "bp.diastolicMinimum", bp.diastolicMinimum, v => !Number.isInteger(v), wholeNumber);
  checkField(errors, "bp.diastolicMaximum", bp.diastolicMaximum, v => !Number.isInteger(v), wholeNumber);
};

/**
 * Validate condition parameters form data.
 * @param formData - the current form data
 * @param translate - i18n translate function (full key path)
 */
export const validateConditionParameters = (
  formData: ConditionParametersFormData,
  translate: ValidationTranslate,
  options?: { validateRequired?: boolean; weightUnit?: WeightUnit }
): FieldErrors => {
  const errors: FieldErrors = {};
  const isRequired = options?.validateRequired !== false;

  // Weight values are validated in the unit the user typed them in
  validateWeight(errors, formData.weight, translate, isRequired, options?.weightUnit ?? DEFAULT_WEIGHT_UNIT);
  validateHeartRate(errors, formData.heartRate, translate, isRequired);
  validateBloodPressure(errors, formData.bloodPressure, translate, isRequired);

  // Medication info: required when medication checkbox is checked (only on save)
  if (isRequired && formData.conditions.takesMedicationForWeightGain && !formData.conditions.medicationInfo?.trim()) {
    errors["conditions.medicationInfo"] = translate(`${T_BASE}.medicationInfoRequired`, "Medication info is required.");
  }

  return errors;
};

/**
 * Deep-compare two ConditionParametersFormData objects for value equality.
 * Only compares the editable fields (weight, heartRate, bloodPressure, conditions).
 */
export const hasParameterValuesChanged = (a: ConditionParametersFormData, b: ConditionParametersFormData): boolean => {
  if (a.weight.minimum !== b.weight.minimum) {
    return true;
  }
  if (a.weight.maximum !== b.weight.maximum) {
    return true;
  }
  if (a.weight.maxDelta !== b.weight.maxDelta) {
    return true;
  }
  if (a.heartRate.minimum !== b.heartRate.minimum) {
    return true;
  }
  if (a.heartRate.maximum !== b.heartRate.maximum) {
    return true;
  }
  if (a.bloodPressure.systolicMinimum !== b.bloodPressure.systolicMinimum) {
    return true;
  }
  if (a.bloodPressure.systolicMaximum !== b.bloodPressure.systolicMaximum) {
    return true;
  }
  if (a.bloodPressure.systolicUrgentLow !== b.bloodPressure.systolicUrgentLow) {
    return true;
  }
  if (a.bloodPressure.diastolicMinimum !== b.bloodPressure.diastolicMinimum) {
    return true;
  }
  if (a.bloodPressure.diastolicMaximum !== b.bloodPressure.diastolicMaximum) {
    return true;
  }
  if (a.conditions.hasICD !== b.conditions.hasICD) {
    return true;
  }
  if (a.conditions.hasLVAD !== b.conditions.hasLVAD) {
    return true;
  }
  if (a.conditions.takesMedicationForWeightGain !== b.conditions.takesMedicationForWeightGain) {
    return true;
  }
  if ((a.conditions.medicationInfo ?? "") !== (b.conditions.medicationInfo ?? "")) {
    return true;
  }
  return false;
};
